/**
 * The "Try it on" connect popup, opened by the KuzFit WordPress plugin on a shop page.
 * Pure logic, so it can be tested without a browser: which step to show, and how the
 * shopper's identity is handed back to the shop that opened the popup (and only to it).
 */

export const READY_MESSAGE = 'kuzfit:ready';
export const SIGNOUT_MESSAGE = 'kuzfit:signout';

export const STEPS = {
  CHECKING: 'checking',
  INVALID: 'invalid',
  LOGIN: 'login',
  SELFIE: 'selfie',
  READY: 'ready',
  SETTINGS: 'settings',
};

/**
 * shopKey and origin from the popup URL; origin is normalised to scheme://host. view is 'settings'
 * when the shop's settings control opened the popup, else the usual 'connect' flow.
 */
export const parseConnectParams = (search) => {
  const params = new URLSearchParams(search || '');
  const shopKey = (params.get('shopKey') || '').trim();
  let origin = '';
  try {
    const url = new URL(params.get('origin') || '');
    if (url.protocol === 'https:') origin = url.origin;
  } catch (e) {
    origin = '';
  }
  const view = params.get('view') === 'settings' ? 'settings' : 'connect';
  return { shopKey, origin, view, valid: shopKey !== '' && origin !== '' };
};

/** The step to show, from what is known so far. */
export const nextStep = ({ originChecked, originOk, isLoading, isAuthenticated, hasSelfie, view }) => {
  if (!originChecked) return STEPS.CHECKING;
  if (!originOk) return STEPS.INVALID;
  if (isLoading) return STEPS.CHECKING;
  if (!isAuthenticated) return STEPS.LOGIN;
  if (hasSelfie === undefined) return STEPS.CHECKING;
  if (view === 'settings') return STEPS.SETTINGS;
  return hasSelfie ? STEPS.READY : STEPS.SELFIE;
};

/** The selfies response: versions newest first, and the active one's file id (null without one). */
export const readSelfies = (response) => {
  const pick = (o, name) => (o ? (o[name] !== undefined ? o[name] : o[name.charAt(0).toUpperCase() + name.slice(1)]) : undefined);
  const versions = (pick(response, 'versions') || [])
    .map((v) => ({
      fileId: pick(v, 'fileId'),
      version: pick(v, 'version'),
      active: !!pick(v, 'active'),
      createdUtc: pick(v, 'createdUtc'),
    }))
    .sort((a, b) => b.version - a.version);
  const active = pick(response, 'active');
  return { active: active === undefined ? null : active, versions };
};

/** The preferences response: whether automatic try-ons are on. Off unless it plainly says true. */
export const readPreferences = (response) => {
  const auto = response ? (response.autoTryOn !== undefined ? response.autoTryOn : response.AutoTryOn) : undefined;
  return { autoTryOn: auto === true };
};

/**
 * Gives the shop page that opened the popup the shopper's KuzFit access token (for its try-on
 * images; never who they are), which selfie is active so it can refresh try-ons made from another,
 * and (when known) whether automatic try-ons are on. Only ever to the origin the API confirmed
 * belongs to the shop's key: never '*'. False when there is no opener to tell.
 */
export const notifyShop = (opener, origin, token, selfie, auto) => {
  if (!opener || typeof opener.postMessage !== 'function' || !origin || !token) return false;
  const message = { type: READY_MESSAGE, token };
  if (selfie) message.selfie = selfie;
  if (typeof auto === 'boolean') message.auto = auto;
  opener.postMessage(message, origin);
  return true;
};

/** Tells the shop page to forget the shopper (sign out on this shop only). Same origin rule. */
export const notifySignOut = (opener, origin) => {
  if (!opener || typeof opener.postMessage !== 'function' || !origin) return false;
  opener.postMessage({ type: SIGNOUT_MESSAGE }, origin);
  return true;
};

/** The shop as a shopper knows it: its host without www, or "this shop". */
export const shopName = (origin) => {
  try {
    return new URL(origin).host.replace(/^www\./, '') || 'this shop';
  } catch (e) {
    return 'this shop';
  }
};

const CLOSE_AFTER_SIGN_OUT = 'kuzfit:closeAfterSignOut';

/** Signing out of KuzFit leaves through Auth0 and comes back to the app; the popup asks to be closed then. */
export const closeAfterSignOut = (win) => {
  try {
    win.sessionStorage.setItem(CLOSE_AFTER_SIGN_OUT, '1');
  } catch (e) {
    // Storage blocked: the shopper lands on the app's home page instead.
  }
};

/** Closes the shop's popup when it comes back from logging out; true if it did. */
export const closeIfSignedOutPopup = (win) => {
  let asked = false;
  try {
    asked = win.sessionStorage.getItem(CLOSE_AFTER_SIGN_OUT) === '1';
    if (asked) win.sessionStorage.removeItem(CLOSE_AFTER_SIGN_OUT);
  } catch (e) {
    return false;
  }
  if (!asked || !win.opener) return false;
  win.close();
  return true;
};

export const SESSION_MESSAGE = 'kuzfit:session';
export const CHECK_MESSAGE = 'kuzfit:check';

/** Tells the shop's page (and only that origin) whether this browser is logged in to KuzFit. */
export const notifySession = (parent, origin, { signedIn, token, selfie }) => {
  if (!parent || typeof parent.postMessage !== 'function' || !origin) return false;
  const message = { type: SESSION_MESSAGE, signedIn: !!signedIn };
  if (signedIn) {
    message.token = token;
    if (selfie) message.selfie = selfie;
  }
  parent.postMessage(message, origin);
  return true;
};

/** Whether an Auth0 error means "nobody is logged in" (as opposed to "could not tell"). */
export const isLoggedOutError = (error) =>
  !!error && ['login_required', 'consent_required', 'interaction_required'].includes(error.error);
